"use client";

import { useEffect, useState, useCallback } from "react";
import {
  getAndroidVendorFromUA,
  getFixedAndroidSafeAreaInsetsByVendor,
  getSafeAreaModeFromUA,
} from "@/src/utils/safeAreaPolicy";

/**
 * 安全区域参数接口
 */
export interface SafeAreaInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * 获取当前安全区域值（从 CSS 变量读取）
 */
function getSafeAreaInsetsFromCSS(): SafeAreaInsets {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }

  const root = document.documentElement;
  const style = getComputedStyle(root);

  return {
    top: parseInt(style.getPropertyValue("--safe-area-inset-top") || "0", 10),
    right: parseInt(
      style.getPropertyValue("--safe-area-inset-right") || "0",
      10,
    ),
    bottom: parseInt(
      style.getPropertyValue("--safe-area-inset-bottom") || "0",
      10,
    ),
    left: parseInt(style.getPropertyValue("--safe-area-inset-left") || "0", 10),
  };
}

function getSafeAreaModeRuntime() {
  if (typeof navigator === "undefined") {
    return "dynamic" as const;
  }
  return getSafeAreaModeFromUA(navigator.userAgent || "");
}

function getFixedSafeAreaInsetsRuntime(): SafeAreaInsets {
  if (typeof navigator === "undefined") {
    return { top: 24, right: 0, bottom: 16, left: 0 };
  }
  const vendor = getAndroidVendorFromUA(navigator.userAgent || "");
  return getFixedAndroidSafeAreaInsetsByVendor(vendor);
}

/**
 * 检测是否支持安全区域
 * @returns 如果 CSS 变量存在且不为 0，返回 true
 */
export function isSafeAreaSupported(): boolean {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  const insets = getSafeAreaInsetsFromCSS();
  // 如果至少有一个方向有值，认为支持安全区域
  return (
    insets.top > 0 || insets.right > 0 || insets.bottom > 0 || insets.left > 0
  );
}

/**
 * Hook: 获取安全区域参数
 *
 * 功能：
 * - 从 CSS 变量读取安全区域值
 * - 监听 `safe-area-insets-changed` 事件，自动更新
 * - 返回当前安全区域的 top, right, bottom, left 值
 *
 * @returns SafeAreaInsets 对象，包含四个方向的安全区域值（单位：px）
 *
 * @example
 * const insets = useSafeAreaInsets();
 * // insets = { top: 24, right: 0, bottom: 48, left: 0 }
 * // 使用：style={{ paddingBottom: insets.bottom + 'px' }}
 * // 或使用 CSS 变量：paddingBottom: 'var(--safe-area-inset-bottom, 0px)'
 */
export function useSafeAreaInsets(): SafeAreaInsets {
  const [insets, setInsets] = useState<SafeAreaInsets>(() => {
    // 初始化时读取 CSS 变量
    if (getSafeAreaModeRuntime() === "fixed") {
      return getFixedSafeAreaInsetsRuntime();
    }
    return getSafeAreaInsetsFromCSS();
  });

  // 处理安全区域变化事件
  const handleSafeAreaChange = useCallback((event: Event) => {
    if (getSafeAreaModeRuntime() === "fixed") {
      setInsets(getFixedSafeAreaInsetsRuntime());
      return;
    }
    const customEvent = event as CustomEvent<SafeAreaInsets>;
    if (customEvent.detail) {
      setInsets(customEvent.detail);
    } else {
      // 如果没有 detail，从 CSS 变量重新读取
      setInsets(getSafeAreaInsetsFromCSS());
    }
  }, []);

  useEffect(() => {
    // 初始化时读取一次
    if (getSafeAreaModeRuntime() === "fixed") {
      setInsets(getFixedSafeAreaInsetsRuntime());
    } else {
      setInsets(getSafeAreaInsetsFromCSS());
    }

    // 监听安全区域变化事件
    window.addEventListener("safe-area-insets-changed", handleSafeAreaChange);

    // 可选：监听窗口大小变化和屏幕旋转（作为备用机制）
    // 因为原生端会在这些情况下自动更新，但作为备用可以确保同步
    const handleResize = () => {
      // 延迟一点，等待原生端更新 CSS 变量
      setTimeout(() => {
        if (getSafeAreaModeRuntime() === "fixed") {
          setInsets(getFixedSafeAreaInsetsRuntime());
        } else {
          setInsets(getSafeAreaInsetsFromCSS());
        }
      }, 100);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener(
        "safe-area-insets-changed",
        handleSafeAreaChange,
      );
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [handleSafeAreaChange]);

  return insets;
}

/**
 * Hook: 获取单个方向的安全区域值
 *
 * @param direction 方向：'top' | 'right' | 'bottom' | 'left'
 * @returns 该方向的安全区域值（单位：px）
 *
 * @example
 * const bottomInset = useSafeAreaInset('bottom');
 * // bottomInset = 48
 * // 使用：style={{ paddingBottom: `${bottomInset}px` }}
 */
export function useSafeAreaInset(direction: keyof SafeAreaInsets): number {
  const insets = useSafeAreaInsets();
  return insets[direction];
}
